import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

import classes from "./navigation.module.css";
import DialogPopup from "../../../../components/DialogPopup";
import useNavigationAway from "../../../../hooks/useNavigationAway";
import { useAllTenantsQuery } from "../../../../api/tenants.api.slice";
import { openNewAppDialog } from "../../slices/newApplication.slice";
import { primary, white } from "../../../../theme/color.constants";
import getCreateApplicationIcon from "../../../application/services/createApplication.service";

const switchAccPermission = "switch_account";
const initialTenantProps = {
  name: "",
  limit: 10,
  sort: "name",
  page: 1,
};

function AccountSwitcher(props) {
  const {
    accountList = [],
    handleAccountSwitch,
    currentTenant,
    fetchOnDemand = false,
  } = props;

  const [autoCmpText, setAutoCmpText] = useState("");
  const [autoCmpSelect, setAutoCmpSelect] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sourceType, setSourceType] = useState("");
  const [skip, setSkip] = useState(true);
  const [tenantProps, setTenantProps] = useState(initialTenantProps);
  const showNavigatingAwayAlert = useNavigationAway();

  const { currentData: allTenants, isFetching: loading } = useAllTenantsQuery(
    tenantProps,
    {
      skip: skip || tenantProps.name?.length < 2,
    }
  );
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState(
    accountList.filter((account) => account._id !== currentTenant._id)
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetTenantProps = useCallback(
    debounce((newTenantProps) => setTenantProps(newTenantProps), 700),
    []
  );

  const switchAccount = (ev, value) => {
    if (value && value._id && showNavigatingAwayAlert()) {
      setAutoCmpSelect(value);
      setShowConfirm(true);
    }
  };
  const handleInputChange = (event, value, source) => {
    if (value || source === "input") {
      setAutoCmpText(value);
    }
    setSourceType(source);
  };
  const onConfirmClose = () => {
    setAutoCmpText("");
    setAutoCmpSelect(null);
    setShowConfirm(false);
  };
  const onConfirmOk = () => {
    setShowConfirm(false);
    if (autoCmpSelect && autoCmpSelect._id) {
      handleAccountSwitch(autoCmpSelect._id);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => debouncedSetTenantProps.cancel(), []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (autoCmpText.length < 3 || !fetchOnDemand) {
      setSkip(true);
      setTenantProps(initialTenantProps);
      debouncedSetTenantProps.cancel();
      return undefined;
    }
    setSkip(false);
    if (sourceType !== "reset")
      debouncedSetTenantProps({ ...tenantProps, name: autoCmpText });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCmpText, fetchOnDemand]);
  useEffect(() => {
    let active = true;
    if (active && allTenants && allTenants.docs) {
      setOptions(
        allTenants.docs.filter((account) => account._id !== currentTenant._id)
      );
    }
    return () => {
      active = false;
    };
  }, [allTenants, currentTenant?._id]);
  useEffect(() => {
    if (!open && fetchOnDemand) {
      setOptions([]);
      setAutoCmpText("");
    }
  }, [fetchOnDemand, open]);
  useEffect(() => {
    setAutoCmpText("");
    setAutoCmpSelect(null);
    setOptions(
      accountList.filter((account) => account._id !== currentTenant?._id)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant]);

  const dialogContent = (
    <Typography variant="h6">
      Are you sure you want to switch to a different account?
    </Typography>
  );

  return (
    <>
      <Autocomplete
        onChange={switchAccount}
        onInputChange={handleInputChange}
        sx={{ mt: "1.25rem" }}
        id="combo-box-demo"
        clearOnBlur
        blurOnSelect
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        loading={loading}
        inputValue={autoCmpText}
        value={autoCmpSelect}
        options={options}
        getOptionLabel={(option) => (option.name ? option.name : option._id)}
        renderOption={(optionProps, option) => (
          <li
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...optionProps}
            key={option._id}
            style={{ wordBreak: "break-word" }}
          >
            {option.name}
          </li>
        )}
        style={{ width: "100%" }}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            className={classes.notchedOutline}
            label="Switch Account"
            color="primary"
            variant="outlined"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
      <DialogPopup
        content={dialogContent}
        openPopup={showConfirm}
        onOk={onConfirmOk}
        onCancel={onConfirmClose}
        showTitle={false}
        maxWidth="xs"
      />
    </>
  );
}
AccountSwitcher.propTypes = {
  accountList: PropTypes.oneOfType([PropTypes.array]),
  handleAccountSwitch: PropTypes.func.isRequired,
  currentTenant: PropTypes.oneOfType([PropTypes.object]).isRequired,
  fetchOnDemand: PropTypes.bool,
};
AccountSwitcher.defaultProps = {
  accountList: [],
  fetchOnDemand: false,
};
function AccountCard(props) {
  const {
    tenantList = [],
    switchTenantHandler,
    permissions,
    currentTenant,
  } = props;
  const dispatch = useDispatch();
  const { navConfig } = useSelector((state) => state.navConfig);
  const createApplicationBtnLabel =
    navConfig?.createApplication?.label ?? "Create New Application";
  const CreateApplicationBtnIcon = getCreateApplicationIcon(
    navConfig?.createApplication?.icon
  );

  return (
    <Card
      sx={(theme) => ({
        width: "100%",
        margin: "0 auto",
        boxShadow: "3px 3px 8px 0 #2b2b2b",
        backgroundColor: "#ececec",
        ...theme.applyStyles("dark", {
          backgroundColor: theme.palette.background.paper,
        }),
      })}
    >
      <CardContent>
        <Grid
          container
          direction="row"
          sx={{ justifyContent: "center", alignItems: "center" }}
        >
          <Grid size={4}>
            <AssignmentIndIcon
              sx={(theme) => ({
                color: "#505050",
                height: "100%",
                width: "85%",
                ...theme.applyStyles("dark", { color: "#ffff" }),
              })}
            />
          </Grid>
          <Grid tabIndex={0} size={8}>
            <Typography variant="body2" component="p">
              Selected Account:
            </Typography>
            <Typography
              gutterBottom
              variant="h6"
              component="p"
              sx={{ wordWrap: "break-word" }}
            >
              {currentTenant && currentTenant.name}
            </Typography>
          </Grid>
        </Grid>
        <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
          {(tenantList.length > 0 ||
            permissions?.indexOf(switchAccPermission) !== -1) && (
            <AccountSwitcher
              accountList={tenantList}
              currentTenant={currentTenant}
              handleAccountSwitch={switchTenantHandler}
              fetchOnDemand={permissions?.indexOf(switchAccPermission) !== -1}
            />
          )}
        </Grid>
      </CardContent>
      <CardActions
        sx={(theme) => ({
          backgroundColor: "#ffffff",
          borderTopWidth: "1px",
          borderTopStyle: "solid",
          borderTopColor: "#a6a5a5",
          "& .MuiButton-root > span": {
            color: white,
            ...theme.applyStyles("light", { color: primary }),
          },
          ...theme.applyStyles("dark", {
            backgroundColor: "#393939",
            borderTopColor: "#5e5e5e",
          }),
        })}
      >
        <Grid
          container
          sx={{ width: "100%", justifyContent: "center", alignItems: "center" }}
        >
          <Button
            size="small"
            sx={{ gap: 0.5 }}
            onClick={() => dispatch(openNewAppDialog())}
          >
            <CreateApplicationBtnIcon
              aria-hidden="true"
              color="primary"
              fontSize="small"
            />
            <span>{createApplicationBtnLabel}</span>
          </Button>
        </Grid>
      </CardActions>
    </Card>
  );
}
AccountCard.propTypes = {
  tenantList: PropTypes.oneOfType([PropTypes.array]),
  currentTenant: PropTypes.oneOfType([PropTypes.object]).isRequired,
  switchTenantHandler: PropTypes.func.isRequired,
  permissions: PropTypes.oneOfType([PropTypes.array]).isRequired,
};
AccountCard.defaultProps = {
  tenantList: [],
};
export default AccountCard;
